/**
 * Design tokens — the single source of truth for the portal's visual language.
 *
 * Everything else in this package derives from here. If a colour, radius or
 * spacing value is needed anywhere, it comes from this file rather than being
 * written inline. That is what stops the four modules drifting apart again.
 */

/** Primary ramp, built around the accent the legacy apps already used: #4E74E5. */
export const brand = {
  50: '#EEF2FD',
  100: '#D8E1FB',
  200: '#B4C5F6',
  300: '#8BA5F0',
  400: '#6A8BEA',
  500: '#4E74E5',
  600: '#3A5CCB',
  700: '#2F49A3',
  800: '#27397D',
  900: '#1F2C5C',
} as const;

export const neutral = {
  0: '#FFFFFF',
  25: '#FBFCFE',
  50: '#F6F8FC',
  100: '#EDF0F6',
  200: '#E4E8F0',
  300: '#CBD2DF',
  400: '#9AA4B8',
  500: '#6B7689',
  600: '#4D5768',
  700: '#374052',
  800: '#232B3A',
  900: '#171E2E',
  950: '#0F1420',
} as const;

/**
 * Status colours. Each has a main (text/icon on light surfaces), a soft
 * background for chips and banners, and a dark variant for dark mode.
 * All main-on-white and dark-on-dark pairs meet WCAG AA for body text.
 */
export const status = {
  success: { main: '#2E9E6B', soft: '#E6F4EE', dark: '#5BC08F' },
  warning: { main: '#D98A0B', soft: '#FDF2E0', dark: '#EDAE45' },
  error: { main: '#D64545', soft: '#FBEAEA', dark: '#EE7A7A' },
  info: { main: '#3B82C4', soft: '#E7F0F9', dark: '#6BA8E0' },
} as const;

/** 8px grid. MUI's spacing(n) returns n * 8px. */
export const SPACING_UNIT = 8;

export const radius = {
  sm: 6,
  md: 10,
  lg: 12,
  xl: 16,
  pill: 999,
} as const;

/**
 * One soft elevation, used sparingly. Enterprise tools read as cluttered when
 * every surface floats, so cards use a 1px border by default and reserve
 * shadow for genuinely floating things: menus, dialogs, popovers.
 */
export const shadow = {
  none: 'none',
  card: '0 1px 2px 0 rgba(23, 30, 46, 0.04)',
  raised: '0 4px 12px -2px rgba(23, 30, 46, 0.10), 0 2px 4px -2px rgba(23, 30, 46, 0.06)',
  overlay: '0 16px 32px -8px rgba(23, 30, 46, 0.18), 0 4px 8px -4px rgba(23, 30, 46, 0.10)',
} as const;

export const layout = {
  sidebarWidth: 260,
  sidebarCollapsedWidth: 72,
  topbarHeight: 60,
  contentMaxWidth: 1600,
} as const;

export const fontFamily = {
  /**
   * IBM Plex Sans Arabic sits after Inter so Arabic glyphs — which already exist
   * in the attendance and devices data — render properly even though the UI copy
   * is English. Both are installed via @fontsource, not a CDN: this runs inside
   * a hospital network where external font requests may be blocked.
   */
  sans: "'Inter Variable', 'Inter', 'IBM Plex Sans Arabic', system-ui, -apple-system, 'Segoe UI', sans-serif",
  mono: "'SFMono-Regular', 'Consolas', 'Liberation Mono', monospace",
} as const;

/** Base 14px — denser than the 16px web default, which suits all-day operational use. */
export const FONT_SIZE_BASE = 14;

export type Brand = typeof brand;
export type Neutral = typeof neutral;
export type StatusTokens = typeof status;
